import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { Button, Loader } from '../../components'

const T = 'found_stock'

// One frame for the three settlement dialogs (ResolveReviewDialog's shape): no backdrop dismiss,
// X and Cancel frozen while a request is in flight, one error line above the buttons.
export const FoundStockDialogShell = ({
  open,
  title,
  summary,
  product = null,
  productId = null,
  children,
  busy = false,
  errorMessage = null,
  errorAction = null,
  confirmLabel,
  cancelLabel,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const { i18n } = useTranslation()
  // Spec §7.1: every settlement names the product it acts on. Ingresar bills and emails the
  // merchant, and the modal covers the queue row that showed the SKU, so it is stated here too.
  const showsProduct = product != null || productId != null
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => {}}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg sm:w-full p-6 text-gray-600 my-3">
              <Loader show={busy} />
              <Dialog.Title className="text-lg text-center mb-2">{title}</Dialog.Title>
              {summary && <p className={`text-sm text-center text-gray-500 ${showsProduct ? 'mb-1' : 'mb-4'}`}>{summary}</p>}
              {showsProduct && (
                <p className="text-sm text-center mb-4" data-testid="found-stock-dialog-product">
                  <span className="text-gray-400">{i18n.t(`${T}.dialog_product_label`)}: </span>
                  {product ? (
                    <>
                      <span className="font-medium text-gray-900">{product.name}</span>
                      {(product.skus || []).length > 0 && <span className="text-gray-500"> · {product.skus.join(', ')}</span>}
                    </>
                  ) : (
                    i18n.t(`${T}.queue.product_fallback`, { id: productId })
                  )}
                </p>
              )}
              {children}
              {errorMessage && (
                <p className="text-sm text-red-600 mt-3 text-center" role="alert">
                  {errorMessage}
                  {/* Bare: the action owns its own spacing, so one that renders nothing leaves no box. */}
                  {errorAction}
                </p>
              )}
              <div className="mt-4 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel} disabled={busy}>
                  {cancelLabel}
                </Button>
                <Button className="m-1" type="primary" onClick={onConfirm} disabled={busy || confirmDisabled} loading={busy}>
                  {confirmLabel}
                </Button>
              </div>
              {/* Last in DOM order, absolutely positioned top-right: the focus trap then lands on the
                  first field instead of the close button, where Enter would cancel the dialog. */}
              <button
                type="button"
                aria-label={i18n.t(`${T}.dialog_close`)}
                className="absolute top-0 right-0 p-5 cursor-pointer disabled:opacity-50"
                disabled={busy}
                onClick={onCancel}
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
